import { createContext, useContext, useEffect, useState } from "react";
import type { Me } from "./api/index.js";
import { navigate, usePathname, useUrlFilters } from "./url-filters.js";

/**
 * Which workspace the app is showing, when an account has more than one.
 *
 * An invite puts someone in a second workspace, and the app used to show
 * `workspaces[0]` and nothing else. The choice is this browser's (DESIGN.md
 * §9, the workspace switcher): kept in localStorage per user, because it is a
 * preference about this screen rather than server state or a filter, and a
 * stale one simply falls back to the first workspace still theirs.
 *
 * An invite lands with `?workspace=<id>` so the joined workspace opens first;
 * that choice is taken, kept, and removed from the address.
 */

export type Membership = Me["workspaces"][number];

const storageKey = (userId: string) => `crux.workspace.${userId}`;

function initialChoice(userId: string, fromInvite: string | null): string | null {
  if (fromInvite !== null) {
    window.localStorage.setItem(storageKey(userId), fromInvite);
    return fromInvite;
  }
  return window.localStorage.getItem(storageKey(userId));
}

export function useChosenWorkspace(
  userId: string,
  workspaces: readonly Membership[],
): {
  readonly current: Membership | null;
  readonly choose: (workspaceId: string) => void;
} {
  const fromInvite = useUrlFilters().params.get("workspace");
  const path = usePathname();
  const [chosen, setChosen] = useState(() => initialChoice(userId, fromInvite));

  // The address is tidied once the choice is read, so a reload or a shared
  // link does not keep re-selecting it. Not data fetching — a URL rewrite.
  useEffect(() => {
    if (fromInvite !== null) navigate(path, { replace: true });
  }, [fromInvite, path]);

  const current =
    workspaces.find((workspace) => workspace.workspaceId === chosen) ??
    workspaces[0] ??
    null;

  return {
    current,
    choose: (workspaceId) => {
      window.localStorage.setItem(storageKey(userId), workspaceId);
      setChosen(workspaceId);
    },
  };
}

/** The workspace on screen and who is looking at it, for screens outside a report. */
export interface WorkspaceView {
  readonly workspaceId: string;
  readonly role: string;
  readonly userId: string;
}

export const WorkspaceContext = createContext<WorkspaceView | null>(null);

export function useWorkspaceView(): WorkspaceView {
  const value = useContext(WorkspaceContext);
  if (value === null) {
    throw new Error("useWorkspaceView is only for screens inside a workspace");
  }
  return value;
}

/**
 * The way to another workspace, for screens that draw no rail — setup, which
 * takes the whole window. Without it, opening a new workspace left nothing on
 * screen that led back to the old one. Null when there is nowhere else to go.
 */
export interface WorkspaceSwitch {
  readonly current: Membership;
  readonly workspaces: readonly Membership[];
  readonly choose: (workspaceId: string) => void;
}

export const WorkspaceSwitchContext = createContext<WorkspaceSwitch | null>(null);

/** The workspace this browser last showed this person, outside the app's shell. */
export function lastChosenWorkspace(userId: string): string | null {
  return window.localStorage.getItem(storageKey(userId));
}
